import './Logo.css';

export interface LogoProps {
  /** The product name: shown beside the mark, and the logo's accessible name. */
  name: string;
  /** `mark` draws the mark alone where the name doesn't fit. The name stays in
   * the page for screen readers. */
  variant?: 'full' | 'mark';
}

/**
 * Logo is the Rootsight mark — a lens over a tree of devices, seeing what the
 * fleet is rooted in — and the product name beside it. Use it wherever the
 * product introduces itself: the console rail and top bar, the sign-in and
 * error pages. It sizes itself from the surrounding font size.
 *
 * Not for an icon inside the product: use a lucide icon. The favicons in
 * packages/ui/public draw the same mark in fixed colours; change both together.
 */
export function Logo({ name, variant = 'full' }: LogoProps) {
  return (
    <span className="rs-logo" data-variant={variant}>
      <svg className="rs-logo__mark" viewBox="0 0 32 32" aria-hidden="true" focusable="false">
        <rect className="rs-logo__tile" width="32" height="32" rx="8" />
        <circle className="rs-logo__line" cx="16" cy="10" r="4.75" strokeWidth="2.5" />
        <path
          className="rs-logo__line"
          d="M16 14.75V18.5M8.5 23V18.5H23.5V23M16 18.5V23"
          strokeWidth="2.25"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <g className="rs-logo__dot">
          <circle cx="16" cy="10" r="1.75" />
          <circle cx="8.5" cy="24" r="2.5" />
          <circle cx="16" cy="24" r="2.5" />
          <circle cx="23.5" cy="24" r="2.5" />
        </g>
      </svg>
      <span className="rs-logo__name">{name}</span>
    </span>
  );
}
